import React, { useState } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import EnrollmentService from "../../../_services/EnrollmentService";

interface GenerateInvoiceModalProps {
  show: boolean;
  onHide: () => void;
  enrollmentId: number;
  onSuccess: () => void;
}

const GenerateInvoiceModal: React.FC<GenerateInvoiceModalProps> = ({
  show,
  onHide,
  enrollmentId,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [pdfLoading, setPdfLoading] = useState<Record<number, boolean>>({});

  const [invoiceDate, setInvoiceDate] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [remarks, setRemarks] = useState("");

  const handleClose = () => {
    setError(null);
    setResult(null);
    setRemarks("");
    setInvoiceDate(new Date().toISOString().slice(0, 10));
    onHide();
  };

  const handleSubmit = async () => {
    setError(null);
    setResult(null);
    setSubmitting(true);
    try {
      const res: any = await EnrollmentService.generateInvoice(enrollmentId, {
        invoiceDate,
        remarks: remarks || undefined,
      });
      if (res.data?.success) {
        setResult(res.data);
        onSuccess();
      } else {
        const detail = res.data?.error ? ` — ${res.data.error}` : "";
        setError(`${res.data?.message || "Failed to generate invoice"}${detail}`);
      }
    } catch (err: any) {
      const data = err?.response?.data;
      const detail = data?.error ? ` — ${data.error}` : "";
      setError(`${data?.message || err.message || "Failed to generate invoice"}${detail}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGeneratePdf = async (invoiceId: number) => {
    setPdfLoading((prev) => ({ ...prev, [invoiceId]: true }));
    try {
      const res: any = await EnrollmentService.generateInvoicePdf(invoiceId);
      if (res.data?.success && res.data.data?.presignedUrl) {
        window.open(res.data.data.presignedUrl, "_blank");
      } else {
        alert("Failed to generate PDF");
      }
    } catch (err: any) {
      alert("Failed to generate PDF: " + (err.message || "Unknown error"));
    } finally {
      setPdfLoading((prev) => ({ ...prev, [invoiceId]: false }));
    }
  };

  const invoices = result?.data?.invoices || [];

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>Generate Invoice</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: 13 }}>
        {error && <Alert variant="danger">{error}</Alert>}

        {result ? (
          <div>
            <Alert variant="success">{result.message}</Alert>
            {invoices.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  Generated Invoices:
                </div>
                {invoices.map((inv: any) => (
                  <div
                    key={inv.invoiceId}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginBottom: 6,
                      padding: "4px 8px",
                      background: "var(--surface-page)",
                      borderRadius: 4,
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>
                      {inv.invoiceNumber}
                    </span>
                    <span style={{ color: "var(--text-muted)" }}>{inv.type}</span>
                    <span>{inv.amount?.toLocaleString()}</span>
                    <Button
                      variant="secondary"
                      size="sm"
                      style={{ fontSize: 10, padding: "1px 6px", marginLeft: "auto" }}
                      disabled={pdfLoading[inv.invoiceId]}
                      onClick={() => handleGeneratePdf(inv.invoiceId)}
                    >
                      {pdfLoading[inv.invoiceId] ? (
                        <><Spinner role="status" size="sm" animation="border" /> PDF...</>
                      ) : (
                        <><i className="fa fa-file-pdf-o" /> Download PDF</>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Invoice Date</Form.Label>
              <Form.Control
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Remarks (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Optional remarks for the invoice"
              />
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={handleClose}>
          {result ? "Close" : "Cancel"}
        </Button>
        {!result && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <><Spinner role="status" size="sm" animation="border" /> Generating...</>
            ) : (
              "Generate Invoice"
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default GenerateInvoiceModal;
